<template>
  <van-popup
    v-model="innerVisible"
    position="bottom"
    round
    :close-on-click-overlay="false"
    get-container="body"
    class="ai-summary-popup"
  >
    <div class="dialog-header">
      <div class="dialog-title">{{ resolvedTitle }}</div>
    </div>

    <div class="dialog-body">
      <div v-if="loading" class="status-text">{{ $t('ai_summary_generating') }}</div>
      <div v-else-if="errorMessage" class="status-text error-text">{{ errorMessage }}</div>
      <div v-else-if="!summaryText" class="status-text">{{ $t('ai_summary_empty') }}</div>
      <div v-else class="summary-content">
        {{ summaryText }}
      </div>
    </div>

    <div class="dialog-footer">
      <van-button
        round
        type="info"
        color="#aa341b"
        class="footer-btn"
        :disabled="!canConfirm"
        :loading="confirmLoading"
        @click="handleConfirm"
      >
        {{ resolvedConfirmText }}
      </van-button>
    </div>
  </van-popup>
</template>

<script>
export default {
  name: 'AISummaryConfirmDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    confirmText: {
      type: String,
      default: '',
    },
    requestFn: {
      type: Function,
      default: null,
    },
    requestData: {
      type: Object,
      default: () => ({}),
    },
    confirmLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      innerVisible: false,
      loading: false,
      errorMessage: '',
      summaryText: '',
      accumulatedResult: '',
      buffer: '',
      currentRunId: 0,
    };
  },
  computed: {
    canConfirm() {
      return !this.loading && !this.errorMessage && !!this.summaryText;
    },
    resolvedTitle() {
      return this.title || this.$t('ai_visit_summary_title');
    },
    resolvedConfirmText() {
      return this.confirmText || this.$t('btn_confirm');
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.innerVisible = value;
        if (value) {
          this.startStreaming();
        } else {
          this.resetState();
        }
      },
    },
    innerVisible(value) {
      if (value !== this.value) {
        this.$emit('input', value);
      }
    },
  },
  methods: {
    resetState() {
      this.loading = false;
      this.errorMessage = '';
      this.summaryText = '';
      this.accumulatedResult = '';
      this.buffer = '';
    },
    extractTextFromChunk(line) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) return '';

      const jsonStr = trimmed.substring(5).trim();
      if (!jsonStr) return '';

      try {
        const json = JSON.parse(jsonStr);
        return json?.output?.text || '';
      } catch (error) {
        return '';
      }
    },
    updateSummary(newText) {
      if (!newText) return;

      if (this.accumulatedResult && newText.startsWith(this.accumulatedResult)) {
        this.accumulatedResult = newText;
      } else {
        this.accumulatedResult += newText;
      }
      this.summaryText = this.accumulatedResult.trim();
    },
    async startStreaming() {
      if (!this.requestFn) {
        this.errorMessage = this.$t('ai_summary_api_missing');
        return;
      }

      this.resetState();
      this.loading = true;
      const runId = Date.now();
      this.currentRunId = runId;

      try {
        await this.requestFn({
          data: this.requestData,
          onChunk: (chunk) => {
            if (this.currentRunId !== runId || !chunk) return;

            this.buffer += chunk;
            const lines = this.buffer.split('\n');
            this.buffer = lines.pop() || '';

            lines.forEach((line) => {
              this.updateSummary(this.extractTextFromChunk(line));
            });
          },
          onComplete: () => {
            if (this.currentRunId !== runId) return;

            if (this.buffer) {
              this.updateSummary(this.extractTextFromChunk(this.buffer));
            }

            this.buffer = '';
            this.loading = false;

            if (!this.summaryText) {
              this.errorMessage = this.$t('ai_summary_failed_retry');
            }
          },
          onError: () => {
            if (this.currentRunId !== runId) return;

            this.loading = false;
            this.errorMessage = this.$t('ai_summary_failed_retry');
          },
        });
      } catch (error) {
        if (this.currentRunId !== runId) return;

        this.loading = false;
        this.errorMessage = this.$t('ai_summary_failed_retry');
      }
    },
    handleConfirm() {
      if (!this.canConfirm) return;
      this.$emit('confirm', this.summaryText);
    },
  },
};
</script>

<style scoped lang="scss">
.ai-summary-popup {
  width: 100%;
  box-sizing: border-box;
  padding: 20px 16px calc(16px + env(safe-area-inset-bottom));
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #323233;
}

.dialog-body {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-height: 120px;
  max-height: 40vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 14px 12px;
  border-radius: 12px;
  background: #f7f8fa;
}

.status-text {
  font-size: 14px;
  line-height: 22px;
  color: #646566;
  white-space: pre-wrap;
}

.error-text {
  color: #ee0a24;
}

.summary-content {
  width: 100%;
  max-width: 100%;
  font-size: 15px;
  line-height: 24px;
  color: #323233;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.dialog-footer {
  margin-top: 16px;
  width: 100%;
}

.footer-btn {
  width: 100%;
}
</style>
